import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { LifeBuoy, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { EMAIL, SUPPORT_URL } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
    "w-full rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3.5 text-sm text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#0D9488] focus:bg-white";
const labelCls = "mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]";

export const ContactSection = ({ t }) => {
    const [form, setForm] = useState({ name: "", email: "", role: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name.trim(),
                email: form.email.trim(),
                role: form.role.trim() || null,
                message: form.message.trim(),
            });
            toast.success(t.contact.success);
            setForm({ name: "", email: "", role: "", message: "" });
        } catch {
            toast.error(t.contact.error);
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" className="scroll-mt-28 bg-[#F8FAFC] py-24 md:py-32" data-testid="contact-section">
            <div className="mx-auto max-w-2xl px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 34 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
                    className="mb-12 text-center"
                >
                    <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#0D9488] md:text-sm">{t.contact.overline}</p>
                    <h2 className="font-display mt-4 text-4xl leading-tight tracking-tight text-[#0F172A] md:text-5xl">{t.contact.title}</h2>
                    <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-[#475569]">{t.contact.sub}</p>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="relative overflow-hidden rounded-[2rem] border border-[#E2E8F0] bg-white p-8 shadow-[0_30px_80px_rgba(5,37,54,0.08)] md:p-10"
                    data-testid="contact-form"
                >
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label htmlFor="contact-name" className={labelCls}>{t.contact.name}</label>
                            <input
                                id="contact-name"
                                required
                                minLength={2}
                                value={form.name}
                                onChange={set("name")}
                                className={inputCls}
                                data-testid="contact-name-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="contact-email" className={labelCls}>{t.contact.email}</label>
                            <input
                                id="contact-email"
                                type="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                className={inputCls}
                                data-testid="contact-email-input"
                            />
                        </div>
                    </div>
                    <div className="mt-6">
                        <label htmlFor="contact-role" className={labelCls}>{t.contact.role}</label>
                        <input
                            id="contact-role"
                            value={form.role}
                            onChange={set("role")}
                            className={inputCls}
                            data-testid="contact-role-input"
                        />
                    </div>
                    <div className="mt-6">
                        <label htmlFor="contact-message" className={labelCls}>{t.contact.message}</label>
                        <textarea
                            id="contact-message"
                            required
                            minLength={2}
                            rows={4}
                            value={form.message}
                            onChange={set("message")}
                            className={`${inputCls} resize-none`}
                            data-testid="contact-message-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={sending}
                        className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#0D9488] px-7 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#0E4B57] disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid="contact-submit-button"
                    >
                        <Send size={16} />
                        {sending ? t.contact.sending : t.contact.submit}
                    </button>

                    <div className="mt-8 flex flex-col items-center gap-3 border-t border-[#E2E8F0] pt-6 text-xs text-[#64748B] md:flex-row md:justify-between">
                        <span>{t.contact.direct}</span>
                        <div className="flex items-center gap-5">
                            <a
                                href={`mailto:${EMAIL}`}
                                className="flex items-center gap-2 transition-colors duration-300 hover:text-[#D97706]"
                                data-testid="contact-email-link"
                            >
                                <Mail size={14} />
                                {EMAIL}
                            </a>
                            <a
                                href={SUPPORT_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 transition-colors duration-300 hover:text-[#D97706]"
                                data-testid="contact-support-link"
                            >
                                <LifeBuoy size={14} />
                                {t.contact.support}
                            </a>
                        </div>
                    </div>
                </motion.form>
            </div>
        </section>
    );
};
